/**
 * First login only (§8): screen calibration, setup guidance, and the
 * metabolic-capacity education screen.
 *
 * All three are re-reachable afterwards from the help link — §8 requires the
 * education screen to be, and a patient who has changed monitors needs the
 * calibration again.
 *
 * The maths is NOT here. The client measures two physical things — how wide a
 * bank card is in pixels on this screen, and roughly how far away the patient
 * sits — and the server turns them into pixels per degree. One implementation,
 * on the side that is the authority for what a prescription in degrees means.
 */

import { useEffect, useRef, useState } from 'react';
import { api } from '../../api.js';
import { EMC_EDUCATION, SETUP_GUIDANCE } from './copy.js';
import styles from './Onboarding.module.css';

/**
 * ID-1, the standard bank card: 85.60 × 53.98 mm. Everyone has one, nobody has
 * to find a ruler, and it is the same size in every country.
 *
 * These two are the physical dimensions of a real object and are not design
 * tokens — the rectangle on screen is sized FROM them, so they belong with the
 * code that draws it rather than in the theme.
 */
const CARD_WIDTH_MM = 85.6;
const CARD_ASPECT = 53.98 / 85.6;

/** A typical 96 px/inch screen, which is where most patients will end up. */
const DEFAULT_CARD_WIDTH_PX = 323;
const CARD_WIDTH_RANGE = { min: 150, max: 900 };

const DEFAULT_DISTANCE_IN = 24;
const DISTANCE_RANGE_IN = { min: 12, max: 48 };

const MM_PER_INCH = 25.4;

export function CalibrationStep({
  onDone,
  onCancel,
}: {
  onDone: () => void;
  onCancel?: () => void;
}) {
  const [widthPx, setWidthPx] = useState(DEFAULT_CARD_WIDTH_PX);
  const [distanceIn, setDistanceIn] = useState(DEFAULT_DISTANCE_IN);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sliderRef = useRef<HTMLInputElement | null>(null);

  // Arrow keys nudge by a single pixel wherever focus is, so the last few
  // pixels of the match do not depend on dragging a slider accurately — which
  // is exactly the kind of fine motor task this population finds hardest.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (document.activeElement === sliderRef.current) return;
      if (event.key === 'ArrowLeft') setWidthPx((w) => Math.max(CARD_WIDTH_RANGE.min, w - 1));
      else if (event.key === 'ArrowRight')
        setWidthPx((w) => Math.min(CARD_WIDTH_RANGE.max, w + 1));
      else return;
      event.preventDefault();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await api.post('/therapy/calibration', {
        pxPerInch: widthPx / (CARD_WIDTH_MM / MM_PER_INCH),
        viewingDistanceIn: distanceIn,
        screenWidthPx: window.screen.width,
        screenHeightPx: window.screen.height,
        devicePixelRatio: window.devicePixelRatio || 1,
      });
      onDone();
    } catch {
      setError(
        'That did not look like a card measurement. Check the rectangle matches your card and try again.',
      );
      setSaving(false);
    }
  }

  return (
    <section className={styles.step}>
      <h1 className={styles.heading}>Let’s set up your screen</h1>
      <p className={styles.lede}>
        This tells the exercises how big your screen really is, so the targets
        are the right size for you. It takes about a minute and you only do it
        once on this computer.
      </p>

      <ol className={styles.instructions}>
        <li>Hold any bank card or driving licence flat against the screen.</li>
        <li>Drag the slider until the outline is exactly the size of your card.</li>
        <li>Use the left and right arrow keys for the last small adjustments.</li>
      </ol>

      <div className={styles.cardArea}>
        <div
          className={styles.cardOutline}
          style={{ width: `${widthPx}px`, height: `${widthPx * CARD_ASPECT}px` }}
          aria-hidden="true"
        />
      </div>

      <label className={styles.label} htmlFor="card-width">
        Card size
      </label>
      <input
        ref={sliderRef}
        id="card-width"
        className={styles.slider}
        type="range"
        min={CARD_WIDTH_RANGE.min}
        max={CARD_WIDTH_RANGE.max}
        step={1}
        value={widthPx}
        onChange={(e) => setWidthPx(Number(e.target.value))}
      />

      <hr className={styles.rule} />

      <label className={styles.label} htmlFor="distance">
        About how far are you sitting from the screen?
      </label>
      <p className={styles.help}>
        An arm’s length is about right for most people. A rough answer is fine —
        what matters is sitting about the same distance every time.
      </p>
      <div className={styles.distanceRow}>
        <input
          id="distance"
          className={styles.slider}
          type="range"
          min={DISTANCE_RANGE_IN.min}
          max={DISTANCE_RANGE_IN.max}
          step={1}
          value={distanceIn}
          onChange={(e) => setDistanceIn(Number(e.target.value))}
        />
        <output className={styles.distanceValue}>
          {distanceIn} in <span className={styles.muted}>({Math.round(distanceIn * 2.54)} cm)</span>
        </output>
      </div>

      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}

      <div className={styles.actions}>
        {onCancel && (
          <button type="button" className={styles.secondary} onClick={onCancel}>
            Keep my current setup
          </button>
        )}
        <button type="button" className={styles.primary} onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'That matches my card'}
        </button>
      </div>
    </section>
  );
}

export function GuidanceStep({ onDone }: { onDone: () => void }) {
  return (
    <section className={styles.step}>
      <h1 className={styles.heading}>Setting yourself up</h1>
      <p className={styles.lede}>
        The same few things every time. Consistency is what makes your results
        mean something.
      </p>

      <dl className={styles.list}>
        {SETUP_GUIDANCE.map((item) => (
          <div key={item.heading} className={styles.listItem}>
            <dt className={styles.listHeading}>{item.heading}</dt>
            <dd className={styles.listBody}>{item.body}</dd>
          </div>
        ))}
      </dl>

      <div className={styles.actions}>
        <button type="button" className={styles.primary} onClick={onDone}>
          Got it
        </button>
      </div>
    </section>
  );
}

/**
 * §8's metabolic-capacity education screen, in the doctor's voice. This is the
 * screen that does the most clinical work in the whole patient side: patients
 * who understand why stopping early matters comply far better than patients
 * following a rule.
 */
export function EducationStep({ onDone }: { onDone: () => void }) {
  const [saving, setSaving] = useState(false);

  async function acknowledge() {
    setSaving(true);
    // Recording that it was read is useful, but not at the cost of the patient
    // being stuck on this screen because the network dropped.
    try {
      await api.post('/therapy/education-seen');
    } catch {
      /* no-op */
    }
    onDone();
  }

  return (
    <section className={styles.step}>
      <h1 className={styles.heading}>Before you start — how this works</h1>

      <dl className={styles.list}>
        {EMC_EDUCATION.map((item) => (
          <div key={item.heading} className={styles.listItem}>
            <dt className={styles.listHeading}>{item.heading}</dt>
            <dd className={styles.listBody}>{item.body}</dd>
          </div>
        ))}
      </dl>

      <div className={styles.actions}>
        <button type="button" className={styles.primary} onClick={acknowledge} disabled={saving}>
          I’ve read this
        </button>
      </div>
    </section>
  );
}
